/**
 * Gives the page working web storage when the browser refuses it. In a
 * cross-site iframe with third-party storage blocked, reading
 * window.localStorage throws a SecurityError, and third-party modules such
 * as mixpanel-browser read it while the bundle is still loading. This file
 * must be the entry's first import so those modules find storage in place.
 * The fallback lives in memory for the life of the page.
 */
const memoryStorage = (): Storage => {
  const items = new Map<string, string>()
  return {
    get length () { return items.size },
    clear: () => { items.clear() },
    getItem: (key: string) => items.get(String(key)) ?? null,
    key: (index: number) => Array.from(items.keys())[index] ?? null,
    removeItem: (key: string) => { items.delete(String(key)) },
    setItem: (key: string, value: string) => { items.set(String(key), String(value)) }
  }
}

const storageDenied = (area: 'localStorage' | 'sessionStorage'): boolean => {
  try {
    return window[area] == null
  } catch {
    return true
  }
}

/**
 * Replaces a denied storage area with an in-memory one. Returns whether it did.
 */
export const installStorageFallback = (area: 'localStorage' | 'sessionStorage'): boolean => {
  if (typeof window === 'undefined' || !storageDenied(area)) return false
  try {
    Object.defineProperty(window, area, { configurable: true, value: memoryStorage() })
    return true
  } catch {
    return false
  }
}

installStorageFallback('localStorage')
installStorageFallback('sessionStorage')
